/**
 * Hook for fetching container stakeholders from the API
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

// ============================================================================
// Types
// ============================================================================

export interface StakeholderInfo {
    role: string;
    company_name: string | null;
    company_id: string | null;
    contact_name: string | null;
    email: string | null;
    phone: string | null;
    address: string | null;
    source: string;
    confidence: number | null;
}

export interface ShipmentStep {
    step_number: number;
    title: string;
    description: string;
    responsible_party: string | null;
    status: 'pending' | 'in_progress' | 'completed';
    estimated_date: string | null;
}

export interface ContactInfo {
    name: string;
    role: string;
    phone: string | null;
    email: string | null;
    priority: number;
}

export interface StakeholdersResponse {
    container_id: string;
    container_number: string;
    stakeholders: StakeholderInfo[];
    shipment_steps: ShipmentStep[];
    useful_contacts: ContactInfo[];
    ai_analysis_performed: boolean;
    last_updated: string;
}

// ============================================================================
// API Function
// ============================================================================

async function fetchContainerStakeholders(
    containerId: string,
    includeAiAnalysis: boolean = false
): Promise<StakeholdersResponse> {
    return apiClient<StakeholdersResponse>(
        `/containers/${containerId}/stakeholders`,
        {
            method: 'GET',
            params: {
                include_ai_analysis: includeAiAnalysis,
            },
        }
    );
}

// ============================================================================
// Hook
// ============================================================================

export function useContainerStakeholders(
    containerId: string | undefined,
    options?: {
        includeAiAnalysis?: boolean;
        enabled?: boolean;
    }
) {
    return useQuery({
        queryKey: ['container-stakeholders', containerId, options?.includeAiAnalysis],
        queryFn: async () => {
            if (!containerId) {
                throw new Error('Container ID is required');
            }
            return fetchContainerStakeholders(containerId, options?.includeAiAnalysis);
        },
        enabled: (options?.enabled ?? true) && !!containerId,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
}
